import { ChangeEvent, useCallback, useEffect, useMemo } from "react";
import { SysCodeDto } from "src/dtos/code/sysCodeDto";
import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";

import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { createMST4010_R01, deleteMST4010_R01, downloadExcel_MST4010_R01, getMST4010_R01AllCount, getMST4010_R01Image, getMST4010_R01List, getMST4010_R01One, patchMST4010_R01 } from "./Api_MST4010_R01";

import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { refetchUpdateList } from "src/utils/axios/reFetch";
import { validateString } from "src/utils/string/stringUtil";
import { showError, showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { useQzPrint } from "src/hooks/useQzPrint";
import { defaultMST4010_R01Dto, MST4010_R01Dto } from "./Dto_MST4010_R01";
import { defaultMST4010_R01Filter, MST4010_R01Filter, useMST4010_R01Store } from "./Store_MST4010_R01";
import { AgGridReact } from "ag-grid-react";
import { fetchDataSetHandler, fetchFilterConfig, gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";
import { yearsToDays } from "date-fns";
import { useCommonCodeValidate } from "src/utils/commonCode/CommonCodeValidateContext";
import { buildCommonCodeRequirements, MST4010_R01_ROW_VALIDATE } from "src/utils/commonCode/commonCodeRequirementConfig";

interface Props {
	gridRef: React.RefObject<AgGridReact<MST4010_R01Dto> | null>;	
	setMessage: (message: string) => void;
	
}

export const Content_MST4010_R01 = (props: Props) => {

	const {
		gridRef,
		setMessage,
	} = props;

	const {
		MST4010_R01Filter, setMST4010_R01Filter,
		MST4010_R01PastFilter, setMST4010_R01PastFilter,
		MST4010_R01List, setMST4010_R01List,
		selectedMST4010_R01, setSelectedMST4010_R01,
		editMST4010_R01, setEditMST4010_R01,
		allCount, setAllCount,
		initData,
		setScrollTop,
	} = useMST4010_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { cycleList, eqmTypeList, eqmStatusList, compCdList, orgCdList, workPlaceList, yesNoList } = useCommonCodeStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();

	useFetchActionbarAuth("MST4010_R01");

	const { printBulkEquipmentLabel } = useQzPrint();

	/** 액션바 출력: 현재 폼(edit) 기준 설비 라벨 1건 (검색영역 라벨 버튼과 동일 데이터 소스) */
	const handlePrintClick = useCallback(async () => {
		if (!editMST4010_R01?.equipmentCd) {
			showWarning('출력할 설비를 선택해주세요.');
			return;
		}
		if (editMST4010_R01.inserted) {
			showWarning('신규 등록 행은 저장 후 라벨을 출력할 수 있습니다.');
			return;
		}

		setLoadingSpinner(true);
		try {
			await printBulkEquipmentLabel([{
				equipmentCd: editMST4010_R01.equipmentCd,
				equipmentNm: editMST4010_R01.equipmentNm ?? '',
				equipmentSpec: editMST4010_R01.equipmentSpec ?? '',
			}]);
		} finally {
			setLoadingSpinner(false);
		}
	}, [editMST4010_R01, printBulkEquipmentLabel, setLoadingSpinner]);
	
	const handleExcelClick = useCallback(async () => {

		const response = await downloadExcel_MST4010_R01(
			'',
			getParams(MST4010_R01Filter),
			MST4010_R01List.length ?? 0
		);
		
		downloadSettingExcel({ responseData: response.data.data, fileName: '설비 기준정보 등록-excel.xlsx' });

	}, [MST4010_R01Filter, MST4010_R01List]);

	const getParams = (filter: MST4010_R01Filter) => {
		const params: any = {};
		if (filter.compCd) params.compCd = filter.compCd;
		if (filter.workplaceCd) params.workplaceCd = filter.workplaceCd;
		if (filter.equipmentType) params.equipmentType = filter.equipmentType;
		if (filter.equipmentCd) params.equipmentCd = filter.equipmentCd;
		return params;
	}

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getMST4010_R01AllCount(getParams(MST4010_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [MST4010_R01Filter, setAllCount]);

	const fetchData = useCallback(async () => {
		setLoadingSpinner(true);
		setScrollTop(null);

		const response = await getMST4010_R01List(
			'search',
			getParams(MST4010_R01Filter),
			MST4010_R01List.length ?? 0
		);
		let newList = [...response.data.data ] as MST4010_R01Dto[];
		setMST4010_R01List(newList);

		const processAllCount = await renewalAllCount();
		
		// 데이터가 있으면 첫 번째 행 선택
		if (newList && newList.length > 0) {
			gridRefSelectedRowHandler(
				gridRef,
				setSelectedMST4010_R01,
				() => true,// () => !data.find(value => value.equipmentCd === editMST4010_R01['equipmentCd'])
			);
			setMST4010_R01PastFilter(MST4010_R01Filter);
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
		} else {
			showWarning('조회된 데이터가 없습니다.');
			handleCreateClick();
		}

		setLoadingSpinner(false);
		setTimeout(() => setMessage(""), 5000);
		return newList;
	}, [MST4010_R01Filter, MST4010_R01List, gridRef, editMST4010_R01, setMST4010_R01List, setSelectedMST4010_R01, setScrollTop])

	const fetchOne = useCallback(async () => {
		if (!selectedMST4010_R01) return;
		setLoadingSpinner(true);
		try {
			const response = await getMST4010_R01One(selectedMST4010_R01.equipmentCd);
			
			const imageResponse = await getMST4010_R01Image(selectedMST4010_R01.equipmentCd);
			setEditMST4010_R01({ 
				...response.data.data,
				eqmImage: imageResponse.data.data.eqmImage || '',
				eqmImage2: imageResponse.data.data.eqmImage2 || '' }
			);
		} catch (error) {
			console.error("조회 중 오류 발생:", error);
			showError("조회 중 오류가 발생했습니다.");
		}
		setLoadingSpinner(false);
	}, [MST4010_R01Filter, setEditMST4010_R01, selectedMST4010_R01]);

	const defaultMST4010_R01Row = useCallback(() => {

		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(MST4010_R01_ROW_VALIDATE, {
				cycleList,
				eqmTypeList,
				eqmStatusList,
				compCdList,
				orgCdList,
				workPlaceList,
				yesNoList,
			}),
		);
		if (!commonResult.valid) { return { ...defaultMST4010_R01Dto }; }

		return {
			...defaultMST4010_R01Dto,
			equipmentType: eqmTypeList[0].minorCd,
			eqmStatus: eqmStatusList[0].minorCd,
			compCd: compCdList[0].compCd,
			eqmOrgcd: orgCdList[0].orgCd,
			workplaceCd: workPlaceList[0].workplaceCd,
			instrucTag: yesNoList[0].minorCd,
			virtualEqm: yesNoList[0].minorCd,
			gatheringCycle: parseInt(cycleList[0].ref01 || '0') || 0,
			inserted: true,
		}
	}, [defaultMST4010_R01Dto, eqmTypeList, eqmStatusList, compCdList, orgCdList, workPlaceList, cycleList, yesNoList, validateCommonCodeLists]);

	const initContainerData = useCallback(() => {
		initData();
	}, [initData]);

	const handleCreateClick = useCallback(async () => {
		setSelectedMST4010_R01(undefined);
		setEditMST4010_R01({
			...defaultMST4010_R01Row(),
		})
	}, [MST4010_R01Filter, selectedMST4010_R01])
	
	const handleDeleteClick = useCallback(async () => {
		if (!selectedMST4010_R01) {
			showWarning('선택된 설비 기준정보이 없습니다.');
			return;
		}

		if (!window.confirm('정말로 삭제하시겠습니까?')) {
			return;
		}

		await deleteMST4010_R01(selectedMST4010_R01.equipmentCd);
		showSuccess(`${selectedMST4010_R01.equipmentCd} 설비 기준정보 삭제가 완료되었습니다.`);
		refetchUpdateList('delete', defaultMST4010_R01Row(), selectedMST4010_R01, 'equipmentCd', MST4010_R01List, setMST4010_R01List);
		handleCreateClick();
	}, [selectedMST4010_R01])

	const validateInsertData = (data: MST4010_R01Dto) => {

		if (!validateString(data.equipmentCd))			{ showWarning("설비 기준정보코드를 입력해주세요.");			return false; }
		if (!validateString(data.equipmentNm))			{ showWarning("설비 기준정보명을 입력해주세요.");			return false; }
		if (!validateString(data.equipmentType))		{ showWarning("설비 기준정보유형을 입력해주세요.");		return false; }
		if (!validateString(data.workplaceCd))			{ showWarning("설비 기준정보 공정을 선택해주세요.");		return false; }
		if (!validateString(data.equipmentSpec))		{ showWarning("설비 기준정보 규격을 입력해주세요.");		return false; }
		if (!validateString(data.compCd))				{ showWarning("설비 기준정보 사업장을 선택해주세요.");	return false; }
		if (!validateString(data.eqmOrgcd))				{ showWarning("설비 기준정보 관리부서를 선택해주세요.");	return false; }
		if (!validateString(data.eqmPerson))			{ showWarning("설비 기준정보 담당자를 선택해주세요.");		return false; }
		if (!data.manufactureDt)						{ showWarning("설비 기준정보 제조일자를 선택해주세요.");		return false; }
		if (!data.purchaseDt)							{ showWarning("설비 기준정보 구매일자를 선택해주세요.");		return false; }
		return true;
	}
	
	const handleSaveClick = useCallback(async () => {

		if(!validateInsertData(editMST4010_R01)) return;

		let processName = '';
		let responseData: MST4010_R01Dto = { ...defaultMST4010_R01Row() };

		if (selectedMST4010_R01) {
			processName = 'patch';
			const response = await patchMST4010_R01(editMST4010_R01);
			responseData = response.data.data[0];
			if (response && response.data) {
				if (response.data.successFlag) {
					showSuccess(`${editMST4010_R01.equipmentCd} 설비 기준정보 수정이 완료되었습니다.`);
					setMessage(`${editMST4010_R01.equipmentCd} 설비 기준정보 수정이 완료되었습니다.`);
				}
			}else return;
		} else {
			processName = 'post';
			const response = await createMST4010_R01(editMST4010_R01);
			responseData = response.data.data[0];
			if (response && response.data) {
				if (response.data.successFlag) {
					showSuccess(`${editMST4010_R01.equipmentCd} 설비 기준정보 등록이 완료되었습니다.`);
					setMessage(`${editMST4010_R01.equipmentCd} 설비 기준정보 등록이 완료되었습니다.`);
				}
			}else return;
		}

		setTimeout(() => {
			setMessage("");
		}, 5000)

		if(editMST4010_R01) {
			refetchUpdateList(processName, responseData, editMST4010_R01, 'equipmentCd', MST4010_R01List, setMST4010_R01List);
		}
	}, [editMST4010_R01]);

	//selectedMST4010_R01 변경 시 설비 이미지 불러오기
	useEffect(() => {
		if (selectedMST4010_R01) {
			fetchOne();
		}else{
			setEditMST4010_R01(defaultMST4010_R01Row());
		}
	}, [selectedMST4010_R01])

	return {
		handleExcelClick,
		handlePrintClick,
		fetchData,
		defaultMST4010_R01Row,
		initContainerData,
		handleCreateClick,
		handleSaveClick,
		handleDeleteClick,
	}

}
